import {
  AmenitiesMusicFestivalMusicFestivalIcon,
  AmenitiesPetsPetFriendlyIcon,
  AmenitiesTailgatingTailgatingIcon,
  Chip,
  EChipSizeVariant,
  GeneralFamilyIcon,
  GeneralHeartUnfilledIcon,
  GeneralTagIcon,
  RideConvertibleIcon,
  VehiclesVehiclesCamperVanSimpleIcon,
  VehiclesVehiclesToyHaulerSimpleIcon,
} from '@outdoorsyco/bonfire';
import React, { SVGProps } from 'react';
import { useIntl } from 'react-intl';

import { useShowRideLinks } from '@/hooks/useShowRideLinks';

import { ERecommendationsMode } from './useRecommendations';

export enum ETagSlug {
  // Not a backend tag_slug — switches the recommendations to auto (car) inventory from Ride
  Cars = 'cars',
  GuestFavorite = 'guest-favorite',
  GreatValue = 'great-value',
  Vintage = 'vintage',
  PetFriendly = 'pet-friendly',
  Campervan = 'campervan',
  SmallTrailer = 'small-trailer',
  Festival = 'festival-friendly',
  Tailgate = 'tailgate-friendly',
  Families = 'family-friendly',
}

type TTagsProps = {
  active: ETagSlug;
  onChange: (tag: ETagSlug) => void;
  mode?: ERecommendationsMode;
};

type TRecommendationTag = {
  tag: ETagSlug;
  label: string;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};

// Bonfire's Ride icon set hardcodes fill="black" instead of currentColor, so the
// icon vanishes on the active (black) chip — force the path to follow text color.
const CarsChipIcon = (props: SVGProps<SVGSVGElement>) => (
  <RideConvertibleIcon {...props} className="[&_path]:fill-current" />
);

export const Tags = ({ active, onChange, mode = ERecommendationsMode.RENTALS }: TTagsProps) => {
  const intl = useIntl();
  const showRideLinks = useShowRideLinks();

  const homePageTags: TRecommendationTag[] = [
    {
      tag: ETagSlug.GuestFavorite,
      label: intl.formatMessage({ defaultMessage: 'Popular', id: 'pxF+t0' }),
      icon: GeneralHeartUnfilledIcon,
    },
    {
      tag: ETagSlug.Campervan,
      label: intl.formatMessage({ defaultMessage: 'Camper van', id: 'nddYs1' }),
      icon: VehiclesVehiclesCamperVanSimpleIcon,
    },
    {
      tag: ETagSlug.PetFriendly,
      label: intl.formatMessage({ defaultMessage: 'Pets', id: 'JH2EfS' }),
      icon: AmenitiesPetsPetFriendlyIcon,
    },
    {
      tag: ETagSlug.GreatValue,
      label: intl.formatMessage({ defaultMessage: 'Value', id: 'GufXy5' }),
      icon: GeneralTagIcon,
    },
    {
      tag: ETagSlug.Vintage,
      label: intl.formatMessage({ defaultMessage: 'Vintage', id: '0hWrM1' }),
      icon: VehiclesVehiclesCamperVanSimpleIcon,
    },
    {
      tag: ETagSlug.SmallTrailer,
      label: intl.formatMessage({ defaultMessage: 'Small trailer', id: 'plzc+y' }),
      icon: VehiclesVehiclesToyHaulerSimpleIcon,
    },
    {
      tag: ETagSlug.Cars,
      label: intl.formatMessage({
        id: '3B6nnv',
        defaultMessage: 'Cars',
        description: 'Homepage recommendations > tag chip that switches to Ride car inventory',
      }),
      icon: CarsChipIcon,
    },
  ];

  const deliveryTags: TRecommendationTag[] = [
    {
      tag: ETagSlug.GuestFavorite,
      label: intl.formatMessage({ defaultMessage: 'Popular', id: 'pxF+t0' }),
      icon: GeneralHeartUnfilledIcon,
    },
    {
      tag: ETagSlug.Festival,
      label: intl.formatMessage({ defaultMessage: 'Festival', id: 'tEppTN' }),
      icon: AmenitiesMusicFestivalMusicFestivalIcon,
    },
    {
      tag: ETagSlug.Tailgate,
      label: intl.formatMessage({ defaultMessage: 'Tailgate', id: 'sTH8UO' }),
      icon: AmenitiesTailgatingTailgatingIcon,
    },
    {
      tag: ETagSlug.GreatValue,
      label: intl.formatMessage({ defaultMessage: 'Value', id: 'GufXy5' }),
      icon: GeneralTagIcon,
    },
    {
      tag: ETagSlug.Families,
      label: intl.formatMessage({ defaultMessage: 'Families', id: 'bkeZ+E' }),
      icon: GeneralFamilyIcon,
    },
    {
      tag: ETagSlug.PetFriendly,
      label: intl.formatMessage({ defaultMessage: 'Pets', id: 'JH2EfS' }),
      icon: AmenitiesPetsPetFriendlyIcon,
    },
  ];

  // The Cars chip swaps the whole carousel to Ride inventory, so it goes away
  // wherever Ride has no cars — that also removes the tile, view-all, and
  // "near {location}" Ride links, which all hang off the chip being active.
  const tags = (mode === ERecommendationsMode.DELIVERY ? deliveryTags : homePageTags).filter(
    ({ tag }) => tag !== ETagSlug.Cars || showRideLinks,
  );

  return (
    <div className="flex px-6 py-4 -mx-6 overflow-auto lg:py-8 gap-2 lg:gap-4 lg:-mx-20 lg:px-20 scrollbar-none section-animated-block">
      {tags.map(tag => {
        return (
          <Chip
            key={tag.tag}
            withBoxShadow={false}
            content={tag.label}
            leftIcon={<tag.icon />}
            active={tag.tag === active}
            onClick={() => onChange(tag.tag)}
            sizeVariant={EChipSizeVariant.Small}
            className="px-4 py-3 bg-white border-white lg:px-6 !h-[38px] lg:!h-[56px] whitespace-nowrap"
          />
        );
      })}
    </div>
  );
};
